import React, { useState, useEffect } from "react";
import {
    Modal,
    Form,
    Input,
    Button,
    Card,
    DatePicker,
    Select,
    Upload,
    Tooltip,
    Row,
    Col,
    message,
} from "antd";
import {
    UploadOutlined,
    CloseOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SuccessModal from "../Events/SuccessModal";
import api from "../../../api";

const { TextArea } = Input;
const { Option } = Select;

const CreateDonation = ({ visible, onCancel, handleCancel, editdata }) => {
    const [form] = Form.useForm();
    const [imagePreview, setImagePreview] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [hasGoal, setHasGoal] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();


    // After your imports
    useEffect(() => {
        if (editdata) {
            form.setFieldsValue({
                title: editdata.title,
                description: editdata.description,
                goalAmount: editdata.amount,
                location: editdata.location,
                contact: editdata.contact,
                email: editdata.email,
                status: editdata.status,
                startDate: editdata.startDate
                    ? dayjs(editdata.startDate, "MM/DD/YYYY")
                    : null,
                endDate: editdata.endDate
                    ? dayjs(editdata.endDate, "MM/DD/YYYY")
                    : null,
            });

            // ✅ AUTO SET GOAL
            if (editdata.amount && Number(editdata.amount) > 0) {
                setHasGoal("yes");
            } else {
                setHasGoal("no");
            }

            if (editdata.image) {
                setImagePreview(editdata.image);
                setFileList([{ name: "existing", url: editdata.image }]);
            }
        } else {
            // reset when creating new
            form.resetFields();
            setHasGoal(null);
            setImagePreview(null);
            setFileList([]);
        }
    }, [editdata]);

    const getBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    const validateImage = (file) => {
        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
        ];

        if (!allowedTypes.includes(file.type)) {
            messageApi.error(
                "Only JPG, JPEG and PNG image formats are allowed."
            );
            return Upload.LIST_IGNORE;
        }

        const isValidSize = file.size / 1024 / 1024 <= 5;

        if (!isValidSize) {
            messageApi.error(
                "Image size should not exceed 5 MB."
            );
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const handleUploadChange = ({ fileList }) => {
        const file = fileList[0];
        if (file && validateImage(file.originFileObj)) {
            getBase64(file.originFileObj).then(setImagePreview);
            setFileList([file]);
        } else {
            setFileList([]);
            setImagePreview(null);
        }
    };

    const disabledStartDate = (current) => {
        return current && current < dayjs().startOf("day");
    };

    const disabledEndDate = (current) => {
        const startDate = form.getFieldValue("startDate");
        return (
            current &&
            (current < dayjs().startOf("day") ||
                (startDate && current < startDate))
        );
    };

    const fakeDonationApi = (formData) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    data: {
                        status: "1",
                        message: "Donation created successfully",
                    },
                });
            }, 1000); // simulate delay
        });
    };

    const handleFinish = async (values) => {
        try {
            setLoading(true)
            const formData = new FormData();

            // API Required Fields (MATCHING CURL)

            formData.append("fld_rf_id", editdata?.rf_id ? editdata?.rf_id : "");

            formData.append("fld_donation_id", editdata?.donation_id ? editdata?.donation_id : ""); // you can make dynamic

            formData.append("fld_donation_name", values.title);
            formData.append("fld_donation_title", values.title);

            formData.append(
                "fld_donation_description",
                values.description
            );

            formData.append(
                "fld_goal_ammount",
                values.goalAmount || 0
            );

            formData.append("fld_location", values.location);

            formData.append("fld_contact_no", values.contact);

            formData.append("fld_email_id", values.email || "");

            formData.append(
                "fld_start_date",
                values.startDate
                    ? dayjs(values.startDate).format("MM/DD/YYYY")
                    : ""
            );

            formData.append(
                "fld_end_date",
                values.endDate
                    ? dayjs(values.endDate).format("MM/DD/YYYY")
                    : ""
            );

            formData.append(
                "fld_Donation_creaation_date",
                dayjs().format("MM/DD/YYYY")
            );

            formData.append("fld_loggedin_user_id", "admin");

            // Image (IMPORTANT NAME CHANGE)
            // ✅ FIXED IMAGE HANDLING
            if (fileList[0]?.originFileObj) {
                // New image uploaded
                formData.append("fld_image_path", fileList[0].originFileObj);
            } else if (editdata?.image) {
                // Editing → use existing image URL
                formData.append("fld_image_path", editdata.image);
            } else {
                messageApi.error("Please upload donation image");
                return;
            }

            // Static Fields
            formData.append("synceddatetime", "2025-05-29 10:08:00");
            formData.append("FormCode", "101");
            formData.append("ApiKey", "kavin");
            formData.append("AppTypeNo", "3");
            formData.append("AppVersion", "1.0.0.0");


            // ✅ REAL API CALL
            const response = await api.donationCreation(formData);

            if (response?.data?.status == 1) {
                setShowSuccessModal(true);

                form.resetFields();
                setFileList([]);
                setImagePreview(null);
                setHasGoal(null);
            } else {
                messageApi.error(response?.data?.message || "Failed to create donation");
            }
        } catch (err) {
            console.error(err);
            messageApi.error("Something went wrong");
        } finally {
            setLoading(false)
        }
    };

    const handleCloseModal = () => {

        setShowSuccessModal(false)

    }


    return (
        <>
            {contextHolder}

            <Modal
                open={visible}
                onCancel={onCancel}
                footer={null}
                width={1100}
                centered
                style={{ top: 20, paddingBottom: 20 }}
                styles={{
                    mask: {
                        backdropFilter: "blur(8px)",
                        background: "rgba(0,0,0,0.4)",
                    },
                }}
                closeIcon={<CloseOutlined />}
            >
                <Card>
                    <h2>Create Donation</h2>

                    <Form layout="vertical" form={form} onFinish={handleFinish}>
                        {/* Image Upload */}
                        <Form.Item
                            label="Donation Image"
                            required
                            rules={[{ required: true, message: "Upload image" }]}
                        >
                            <div style={{ height: 200, border: "1px dashed #d9d9d9" }}>
                                {imagePreview ? (
                                    <>
                                        <img src={imagePreview} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        <div style={{ position: "absolute", top: 10, right: 10 }}>
                                            <Tooltip title="Preview">
                                                <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)} />
                                            </Tooltip>
                                            <Tooltip title="Remove">
                                                <Button danger icon={<CloseOutlined />} onClick={() => { setImagePreview(null); setFileList([]); }} />
                                            </Tooltip>
                                        </div>
                                    </>
                                ) : (
                                    <Upload.Dragger
                                        accept=".jpg,.jpeg,.png"
                                        beforeUpload={(file) => {
                                            const isValid = validateImage(file);

                                            if (isValid !== true) {
                                                return Upload.LIST_IGNORE;
                                            }

                                            getBase64(file).then(setImagePreview);

                                            setFileList([
                                                {
                                                    uid: file.uid,
                                                    name: file.name,
                                                    originFileObj: file,
                                                },
                                            ]);

                                            return false;
                                        }}
                                        fileList={fileList}
                                        maxCount={1}
                                        showUploadList={false}
                                    >
                                        <UploadOutlined />
                                        <p>Upload Image</p>

                                        <p style={{ color: "#888", fontSize: 12 }}>
                                            Supported formats: JPG, JPEG, PNG
                                        </p>

                                        <p style={{ color: "#888", fontSize: 12 }}>
                                            Maximum file size: 5 MB
                                        </p>
                                    </Upload.Dragger>
                                )}
                            </div>
                        </Form.Item>

                        {/* Title */}
                        <Form.Item
                            name="title"
                            label="Donation Title"
                            rules={[
                                { required: true, message: "Please enter title" },
                                { min: 3, message: "Minimum 3 characters required" },
                                { max: 150, message: "Maximum 150 characters allowed" },
                            ]}
                        >
                            <Input />
                        </Form.Item>

                        {/* Description */}
                        <Form.Item
                            name="description"
                            label="Description"
                            rules={[
                                { required: true, message: "Please enter description" },
                                { min: 5, message: "Minimum 5 characters required" },
                                { max: 2000, message: "Maximum 2000 characters allowed" },
                            ]}
                        >
                            <TextArea rows={4} maxLength={2000} showCount />
                        </Form.Item>

                        {/* Goal */}
                        <Form.Item label="Does this donation have a goal?">
                            <Select
                                value={hasGoal}
                                placeholder="Select your preference"
                                onChange={(val) => {
                                    setHasGoal(val);

                                    // ✅ If user selects "No", clear goal amount
                                    if (val === "no") {
                                        form.setFieldsValue({ goalAmount: null });
                                    }
                                }}
                            >
                                <Option value="yes">Yes</Option>
                                <Option value="no">No</Option>
                            </Select>
                        </Form.Item>

                        {hasGoal === "yes" && (
                            <Form.Item
                                name="goalAmount"
                                label="Goal Amount"
                                rules={[
                                    { required: true, message: "Enter goal amount" },
                                    {
                                        pattern: /^[0-9]+$/,
                                        message: "Amount must be numeric",
                                    },
                                ]}
                            >
                                <Input type="number" prefix="₹" />
                            </Form.Item>
                        )}

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="location"
                                    label="Location"
                                    rules={[
                                        { required: true, message: "Please enter location" },
                                        { min: 3, message: "Minimum 3 characters required" },
                                        { max: 100, message: "Maximum 100 characters allowed" },
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="contact"
                                    label="Contact Number"
                                    rules={[
                                        { required: true, message: "Please enter contact number" },
                                        {
                                            pattern: /^[0-9]{10}$/,
                                            message: "Enter valid 10 digit mobile number",
                                        },
                                    ]}
                                >
                                    <Input maxLength={10} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        { type: "email", message: "Enter valid email address" },
                                    ]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="status"
                                    label="Status"
                                    rules={[{ required: true, message: "Please select status" }]}
                                >
                                    <Select>
                                        <Option value="Active">Active</Option>
                                        <Option value="Closed">Closed</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="startDate"
                                    label="Start Date"

                                >
                                    <DatePicker style={{ width: "100%" }} disabledDate={disabledStartDate} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="endDate"
                                    label="End Date"

                                >
                                    <DatePicker style={{ width: "100%" }} disabledDate={disabledEndDate} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item >
                            <Row justify="end">
                                <div style={{ display: "flex", gap: "30px" }}>
                                    <Button
                                        onClick={handleCancel}
                                        style={{
                                            width: 150,
                                            height: 40,
                                        }}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        style={{
                                            backgroundColor: "#1980B9",
                                            borderColor: "#1980B9",
                                            width: 150,
                                            height: 40,
                                        }}
                                    >
                                        {editdata ? 'Update Donation' : 'Create Donation'}
                                    </Button>
                                </div>
                            </Row>
                        </Form.Item>
                    </Form>
                </Card>
            </Modal>

            <Modal open={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
                <img src={imagePreview} style={{ width: "100%" }} />
            </Modal>

            {showSuccessModal === true && (
                <SuccessModal
                    visible={showSuccessModal}
                    onClose={handleCancel}
                    title={
                        editdata ? "Donation Updated Successfully" : "Donation Created Successfully"
                    }
                    description={
                        editdata
                            ? "Your donation details has been updated successfully."
                            : "Your donation details has been created and published."
                    }
                />
            )}
        </>
    );
};

export default CreateDonation;